import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader, Section } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { Plus, Trash2, Save, Loader2, RefreshCw, Mail, Triangle } from "lucide-react";

const RECORD_TYPES = ["A", "AAAA", "CNAME", "MX", "TXT", "URL", "URL301", "FRAME", "NS", "CAA"];

const splitDomain = (d) => {
  const parts = d.split(".");
  if (parts.length < 2) return [d, ""];
  return [parts[0], parts.slice(1).join(".")];
};

const DNSManager = () => {
  const [params] = useSearchParams();
  const [domain, setDomain] = useState(params.get("domain") || "");
  const [hosts, setHosts] = useState([]);
  const [nameservers, setNameservers] = useState([]);
  const [usingOurDNS, setUsingOurDNS] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!domain.includes(".")) return;
    const [sld, tld] = splitDomain(domain);
    setLoading(true);
    try {
      const [hostsRes, nsRes] = await Promise.all([
        api.get(`/dns/${sld}/${tld}/hosts`),
        api.get(`/dns/${sld}/${tld}/nameservers`),
      ]);
      setHosts(
        (hostsRes.data.hosts || []).map((h) => ({
          HostName: h.Name || "@",
          RecordType: h.Type || "A",
          Address: h.Address || "",
          TTL: parseInt(h.TTL) || 1800,
          MXPref: parseInt(h.MXPref) || 10,
        }))
      );
      setUsingOurDNS(hostsRes.data.is_using_our_dns);
      setNameservers(nsRes.data.nameservers || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load DNS");
    } finally {
      setLoading(false);
    }
  }, [domain]);

  useEffect(() => {
    if (params.get("domain")) load();
    // eslint-disable-next-line
  }, []);

  const addHost = () =>
    setHosts((h) => [...h, { HostName: "@", RecordType: "A", Address: "", TTL: 1800, MXPref: 10 }]);

  const HOSTINGER_RECORDS = [
    // DKIM (CNAME)
    { HostName: "hostingermail-a._domainkey", RecordType: "CNAME", Address: "hostingermail-a.dkim.mail.hostinger.com.", TTL: 1800, MXPref: 10 },
    { HostName: "hostingermail-b._domainkey", RecordType: "CNAME", Address: "hostingermail-c.dkim.mail.hostinger.com.", TTL: 1800, MXPref: 10 },
    { HostName: "hostingermail-c._domainkey", RecordType: "CNAME", Address: "hostingermail-c.dkim.mail.hostinger.com.", TTL: 1800, MXPref: 10 },
    // Mail servers (MX)
    { HostName: "@", RecordType: "MX", Address: "mx1.hostinger.com.", TTL: 1800, MXPref: 5 },
    { HostName: "@", RecordType: "MX", Address: "mx2.hostinger.com.", TTL: 1800, MXPref: 10 },
    // SPF (TXT)
    { HostName: "@", RecordType: "TXT", Address: "v=spf1 include:_spf.mail.hostinger.com ~all", TTL: 1800, MXPref: 10 },
    // DMARC (TXT)
    { HostName: "_dmarc", RecordType: "TXT", Address: "v=DMARC1; p=none", TTL: 1800, MXPref: 10 },
  ];

  const addHostingerMX = () => {
    setHosts((h) => {
      // Avoid duplicates: skip records that already exist (same Type + Host + Value)
      const exists = (rec) =>
        h.some(
          (r) =>
            r.RecordType === rec.RecordType &&
            (r.HostName || "@") === rec.HostName &&
            (r.Address || "").trim().toLowerCase() === rec.Address.toLowerCase()
        );
      const toAdd = HOSTINGER_RECORDS.filter((rec) => !exists(rec));
      if (toAdd.length === 0) {
        toast.info("Hostinger records already present");
        return h;
      }
      toast.success(`Added ${toAdd.length} Hostinger record(s) — review and click "Save all"`);
      return [...h, ...toAdd.map((r) => ({ ...r }))];
    });
  };

  const updateHost = (i, key, val) =>
    setHosts((h) => h.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));

  const addVercelMX = () => {
    if (!domain.includes(".")) {
      toast.error("Enter a domain first");
      return;
    }
    const vercelRecords = [
      { HostName: "@", RecordType: "A", Address: "216.150.1.1", TTL: 1800, MXPref: 10 },
      { HostName: "www", RecordType: "CNAME", Address: domain, TTL: 1800, MXPref: 10 },
    ];
    setHosts((h) => {
      const exists = (rec) =>
        h.some(
          (r) =>
            r.RecordType === rec.RecordType &&
            (r.HostName || "@") === rec.HostName &&
            (r.Address || "").trim().toLowerCase() === rec.Address.toLowerCase()
        );
      const toAdd = vercelRecords.filter((rec) => !exists(rec));
      if (toAdd.length === 0) {
        toast.info("Vercel records already present");
        return h;
      }
      toast.success(`Added ${toAdd.length} Vercel record(s) — review and click "Save all"`);
      return [...h, ...toAdd.map((r) => ({ ...r }))];
    });
  };

  const removeHost = (i) => setHosts((h) => h.filter((_, idx) => idx !== i));

  const saveHosts = async () => {
    const [sld, tld] = splitDomain(domain);
    setSaving(true);
    try {
      await api.post("/dns/hosts", {
        sld, tld,
        records: hosts.map((h) => ({
          HostName: h.HostName, RecordType: h.RecordType, Address: h.Address,
          TTL: h.TTL, ...(h.RecordType === "MX" ? { MXPref: h.MXPref } : {}),
        })),
      });
      toast.success("DNS records saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveNameservers = async (useDefault = false) => {
    const [sld, tld] = splitDomain(domain);
    setSaving(true);
    try {
      await api.post("/dns/nameservers", { sld, tld, nameservers, use_default: useDefault });
      toast.success(useDefault ? "Default NS restored" : "Custom NS saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Records"
        title="DNS Manager"
        description="View and edit A / AAAA / CNAME / MX / TXT and nameserver settings."
      />
      <Section>
        <Card className="rounded-sm shadow-none border-[var(--border)]">
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[240px]">
                <Label htmlFor="domain" className="text-xs uppercase tracking-wider">
                  Domain
                </Label>
                <Input
                  data-testid="dns-domain-input"
                  id="domain"
                  className="mono mt-1.5 rounded-sm"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value.trim().toLowerCase())}
                  placeholder="example.com"
                  onKeyDown={(e) => e.key === "Enter" && load()}
                />
              </div>
              <Button
                data-testid="btn-load-dns"
                onClick={load}
                disabled={loading || !domain.includes(".")}
                className="brand-bg hover:brand-bg text-white rounded-sm"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Load
              </Button>
            </div>
          </CardContent>
        </Card>

        {hosts.length > 0 || nameservers.length > 0 ? (
          <Tabs defaultValue="hosts" className="mt-6">
            <TabsList className="rounded-sm">
              <TabsTrigger value="hosts" data-testid="tab-hosts">Host Records ({hosts.length})</TabsTrigger>
              <TabsTrigger value="ns" data-testid="tab-ns">Nameservers ({nameservers.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="hosts">
              <div className="border border-[var(--border)] rounded-sm bg-white">
                <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)] border-b border-[var(--border)] bg-[#FAFAFA]">
                  <div className="col-span-2">Type</div>
                  <div className="col-span-2">Host</div>
                  <div className="col-span-5">Value</div>
                  <div className="col-span-1">TTL</div>
                  <div className="col-span-1">MX Pref</div>
                  <div className="col-span-1"></div>
                </div>
                {hosts.length === 0 ? (
                  <div className="p-8 text-center text-sm text-[var(--text-secondary)]">No host records.</div>
                ) : (
                  <div className="divide-y divide-[var(--border)]" data-testid="hosts-list">
                    {hosts.map((h, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center px-4 py-2.5">
                        <div className="col-span-2">
                          <Select
                            value={h.RecordType}
                            onValueChange={(v) => updateHost(i, "RecordType", v)}
                          >
                            <SelectTrigger data-testid={`host-type-${i}`} className="rounded-sm mono h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {RECORD_TYPES.map((t) => (
                                <SelectItem key={t} value={t} className="mono">
                                  {t}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Input
                          data-testid={`host-name-${i}`}
                          className="col-span-2 mono rounded-sm h-9"
                          value={h.HostName}
                          onChange={(e) => updateHost(i, "HostName", e.target.value)}
                        />
                        <Input
                          data-testid={`host-value-${i}`}
                          className="col-span-5 mono rounded-sm h-9"
                          value={h.Address}
                          onChange={(e) => updateHost(i, "Address", e.target.value)}
                        />
                        <Input
                          data-testid={`host-ttl-${i}`}
                          type="number"
                          className="col-span-1 mono rounded-sm h-9"
                          value={h.TTL}
                          onChange={(e) => updateHost(i, "TTL", parseInt(e.target.value) || 1800)}
                        />
                        <Input
                          data-testid={`host-mx-${i}`}
                          type="number"
                          className="col-span-1 mono rounded-sm h-9"
                          value={h.MXPref}
                          disabled={h.RecordType !== "MX"}
                          onChange={(e) => updateHost(i, "MXPref", parseInt(e.target.value) || 10)}
                        />
                        <Button
                          data-testid={`host-delete-${i}`}
                          variant="ghost"
                          size="sm"
                          className="col-span-1 rounded-sm hover:bg-red-50 hover:text-red-600"
                          onClick={() => removeHost(i)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="px-4 py-3 border-t border-[var(--border)] flex gap-2 bg-[#FAFAFA]">
                  <Button
                    data-testid="btn-add-host"
                    variant="outline"
                    size="sm"
                    onClick={addHost}
                    className="rounded-sm"
                  >
                    <Plus className="w-4 h-4 mr-1.5" /> Add record
                  </Button>
                  <Button
                    data-testid="btn-add-hostinger-mx"
                    variant="outline"
                    size="sm"
                    onClick={addHostingerMX}
                    className="rounded-sm"
                    title="Add Hostinger email records: DKIM (3× CNAME) + MX (mx1/mx2) + SPF + DMARC TXT"
                  >
                    <Mail className="w-4 h-4 mr-1.5" /> Add Hostinger MX
                  </Button>
                  <Button
                    data-testid="btn-add-vercel-mx"
                    variant="outline"
                    size="sm"
                    onClick={addVercelMX}
                    className="rounded-sm"
                    title="Add Vercel records: A @ 216.150.1.1 + CNAME www → your domain"
                  >
                    <Triangle className="w-4 h-4 mr-1.5" /> Add Vercel MX
                  </Button>
                  <div className="flex-1" />
                  <Button
                    data-testid="btn-save-hosts"
                    onClick={saveHosts}
                    disabled={saving}
                    className="brand-bg hover:brand-bg text-white rounded-sm"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save all
                  </Button>
                </div>
              </div>
              {!usingOurDNS && (
                <p className="mt-2 text-xs text-[var(--warning)]">
                  ⚠ This domain is not using Namecheap nameservers — host records will be saved but ignored until you switch back.
                </p>
              )}
            </TabsContent>

            <TabsContent value="ns">
              <div className="border border-[var(--border)] rounded-sm bg-white p-6 space-y-3">
                {nameservers.map((ns, i) => (
                  <Input
                    key={i}
                    data-testid={`ns-${i}`}
                    className="mono rounded-sm"
                    value={ns}
                    onChange={(e) =>
                      setNameservers((cur) => cur.map((n, idx) => (idx === i ? e.target.value : n)))
                    }
                  />
                ))}
                <div className="flex gap-2">
                  <Button
                    data-testid="btn-add-ns"
                    variant="outline"
                    size="sm"
                    onClick={() => setNameservers((cur) => [...cur, ""])}
                    className="rounded-sm"
                  >
                    <Plus className="w-4 h-4 mr-1.5" /> Add nameserver
                  </Button>
                  <div className="flex-1" />
                  <Button
                    data-testid="btn-restore-default-ns"
                    variant="outline"
                    onClick={() => saveNameservers(true)}
                    className="rounded-sm"
                  >
                    Restore default
                  </Button>
                  <Button
                    data-testid="btn-save-ns"
                    onClick={() => saveNameservers(false)}
                    disabled={saving}
                    className="brand-bg hover:brand-bg text-white rounded-sm"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save custom
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        ) : null}
      </Section>
    </div>
  );
};

export default DNSManager;
